import { hexToRGBA } from "./ColorHelpers";
import { Segmentation } from "../api-models/response/VideoAnalysisDataResponse";

// Shared segmentation polygon drawing, used by both the video overlay
// (VideoAnalysisPlayer / SegmentationCanvasOverlay) and the detection-image
// overlay (ImageSegmentationOverlay) so damage marking renders identically
// everywhere. The original implementation lived inline in VideoAnalysisPlayer.

export interface SegmentationDrawOptions {
  showArea: boolean;
  showContour: boolean;
  showBox: boolean;
  // Stroke width for contour/box; callers bump this in fullscreen.
  lineWidth?: number;
  // When set, draw this class name on each polygon (top-left of its bounds).
  label?: string;
}

// Draw a set of same-group-code segmentations onto a 2D context.
// scaleX/scaleY convert AI image-space coordinates (segmentationDetails.imageSize)
// to canvas pixels: canvasX = coord.x * scaleX.
export function drawSegmentations(
  context: CanvasRenderingContext2D,
  segmentations: Segmentation[],
  color: string | undefined,
  scaleX: number,
  scaleY: number,
  options: SegmentationDrawOptions
): void {
  if (!segmentations?.length || !color) return;
  const { showArea, showContour, showBox, lineWidth = 2, label } = options;

  for (let i = 0; i < segmentations.length; i++) {
    const coords = segmentations[i].coordinates;
    if (!coords?.length) continue;

    context.beginPath();
    context.lineWidth = lineWidth;
    context.strokeStyle = hexToRGBA(color);
    context.fillStyle = hexToRGBA(color, 0.2);

    for (let j = 0; j < coords.length; j++) {
      context.lineTo(coords[j].x * scaleX, coords[j].y * scaleY);
    }
    context.closePath();

    if (showContour) context.stroke();
    if (showArea) context.fill();

    const xs = coords.map((c) => c.x * scaleX);
    const ys = coords.map((c) => c.y * scaleY);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    if (showBox) {
      context.strokeRect(minX, minY, maxX - minX, maxY - minY);
    }

    if (label) {
      // White text with a dark outline so the class name reads on any background.
      context.font = `bold ${lineWidth >= 4 ? 16 : 12}px Arial`;
      context.textBaseline = "bottom";
      context.lineWidth = lineWidth >= 4 ? 4 : 3;
      context.strokeStyle = "rgba(0,0,0,0.75)";
      context.fillStyle = "#ffffff";
      const ty = Math.max(minY - 3, 13);
      context.strokeText(label, minX, ty);
      context.fillText(label, minX, ty);
    }
  }
}

// From a list of segmentations (already filtered by visible group code + confidence),
// return those whose timestamp is closest to `time` — but only if the closest is
// within `threshold` seconds. Mirrors the video player's closest-frame selection.
// The image overlay uses a looser threshold because a detection's stored timestamp
// rarely matches a segmentation timestamp exactly.
export function closestSegmentationsToTime(
  segmentations: Segmentation[],
  time: number,
  threshold = 0.15
): Segmentation[] {
  if (!segmentations?.length) return [];
  const closestTimestamp = segmentations.reduce((previous, current) =>
    Math.abs(current.timestamp - time) < Math.abs(previous.timestamp - time)
      ? current
      : previous
  ).timestamp;

  if (Math.abs(closestTimestamp - time) > threshold) return [];
  return segmentations.filter((s) => s.timestamp === closestTimestamp);
}
